<script lang="ts">
    import UsersIcon from '../../icons/UsersIcon.svelte';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import FacebookIcon from '../../icons/FacebookIcon.svelte';
    import TwitterIcon from '../../icons/TwitterIcon.svelte';
    import LinkedInIcon from '../../icons/LinkedInIcon.svelte';
    import EmailIcon from '../../icons/EmailIcon.svelte';
    import PetitionEmptyStateIcon from '../../icons/PetitionEmptyStateIcon.svelte';
    import CheckIcon from '../../icons/CheckIcon.svelte';
    import CkEditorContent from '../CkEditorContent.svelte';
    import { getQueryParam } from '../../api/common';
    import { trackPetitionSocialShare } from '../../api/petitions';
    import * as t from '../../translations/Petitions.ts';

    // Type for related petitions
    interface RelatedPetition {
        id: number;
        title: string;
        imageUrl?: string | null;
        signatureCount?: number;
        hasSigned?: boolean;
    }

    export let title: string = "Success!";
    export let message: string = "Thank you for your participation.";
    export let actionType: 'petition' | 'campaign' | 'survey' | 'event' = 'petition';
    export let signatureCount: number | null = null;
    export let signatureGoal: number | null = null;
    export let shareUrl: string = '';
    export let facebookShareUrl: string = '';
    export let twitterShareUrl: string = '';
    export let linkedInShareUrl: string = '';
    export let sharingEnabled: boolean = true;
    export let petitionId: number | null = null;
    export let onBack: () => void = () => {};
    export let onViewOther: () => void = () => {};
    export let onSignRelated: (petition: RelatedPetition) => void = () => {};
    export let thankYouMessage: string = '';
    export let relatedPetitions: RelatedPetition[] = [];
    export let petitionTitle: string = '';
    // Date when user previously signed (if they already signed before)
    export let datePreviouslySigned: string | null = null;
    /** When true, no petition list exists (single-petition flow); hide back to avoid a useless redirect loop. */
    export let hideBackToPetitions: boolean = false;
    
    // Format the date for display (e.g., "Friday, February 13, 2026")
    function formatDate(dateString: string): string {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(undefined, { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch {
            return dateString;
        }
    }

    $: progressPercent = signatureGoal && signatureCount ? Math.min((signatureCount / signatureGoal) * 100, 100) : 0;
    $: signaturesRemaining = signatureGoal && signatureCount
        ? Math.max(signatureGoal - signatureCount, 0)
        : signatureGoal
            ? signatureGoal
            : 0;
    $: actionLabel = actionType === 'petition' ? 'signed' 
        : actionType === 'campaign' ? 'took action'
        : actionType === 'survey' ? 'responded'
        : 'registered';
    $: confirmationMessage = datePreviouslySigned
        ? (petitionTitle ? `You already signed "${petitionTitle}".` : t.Already_Signed_Message())
        : message;
    $: signedDateMessage = datePreviouslySigned ? `Signed on ${formatDate(datePreviouslySigned)}.` : '';

    function safeTranslation(key: string, fallback: string): string {
        const candidate = (t as any)[key];
        if (typeof candidate === 'function') return candidate();
        if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate;
        return fallback;
    }

    function getRemainingSignaturesText(count: number): string {
        return `${count.toLocaleString()} more ${count === 1 ? 'signature' : 'signatures'} needed`;
    }

    function getExceededSignaturesText(count: number): string {
        return `Goal exceeded by ${count.toLocaleString()} ${count === 1 ? 'signature' : 'signatures'}`;
    }

    function copyToClipboard() {
        navigator.clipboard.writeText(shareUrl);
        copied = true;
        setTimeout(() => copied = false, 2000);
    }

    let copied = false;

    function openShareInNewTab(url: string) {
        const popup = window.open(url, '_blank');
        if (popup) {
            popup.opener = null;
        }
    }

    function getShareUserToken(): string | undefined {
        if (!petitionId) {
            return undefined;
        }

        const tokenFromSession = sessionStorage.getItem(`vv-petition-share-user-token-${petitionId}`) || '';
        if (tokenFromSession) {
            return tokenFromSession;
        }

        const tokenFromSettings = (window as any).vvSettings?.userContainer?.userModel?.userToken || '';
        return tokenFromSettings || undefined;
    }

    function getLegacyFingerprint(): string | undefined {
        try {
            const maybeFingerprintFn = (window as any).pstfgrpnt;
            if (typeof maybeFingerprintFn === 'function') {
                const result = String(maybeFingerprintFn(true) ?? '').trim();
                return result || undefined;
            }
        } catch {
            // Ignore fingerprint failures and continue without it.
        }
        return undefined;
    }

    function trackShare(medium: 'Facebook' | 'Twitter' | 'LinkedIn', promoteUrl: string) {
        if (!petitionId) return;
        void trackPetitionSocialShare(petitionId, medium, promoteUrl, {
            referringNugget: getQueryParam('vvsn') || undefined,
            userUrlToken: getShareUserToken(),
            fingerprint: getLegacyFingerprint()
        });
    }

    function shareToFacebook() {
        const targetUrl = facebookShareUrl || shareUrl;
        if (!targetUrl) return;
        const intentUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(targetUrl)}`;
        trackShare('Facebook', targetUrl);
        openShareInNewTab(intentUrl);
    }

    function shareToTwitter() {
        const targetUrl = twitterShareUrl || shareUrl;
        if (!targetUrl) return;
        const text = petitionTitle || `I just ${actionLabel} this petition. Join me!`;
        const intentUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(targetUrl)}&text=${encodeURIComponent(text)}`;
        trackShare('Twitter', targetUrl);
        openShareInNewTab(intentUrl);
    }

    function shareToLinkedIn() {
        const targetUrl = linkedInShareUrl || shareUrl;
        if (!targetUrl) return;
        const title = petitionTitle || '';
        const summary = '';
        const intentUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(targetUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`;
        trackShare('LinkedIn', targetUrl);
        openShareInNewTab(intentUrl);
    }

    function shareViaEmail() {
        const targetUrl = shareUrl || '';
        const bodyText = petitionTitle || `I just ${actionLabel} this petition.`;
        const body = `${bodyText}\n\n${targetUrl}`;
        window.location.href = `mailto:?body=${encodeURIComponent(body)}`;
    }
</script>

<div class="confirmation-shell flex flex-col items-center text-center pt-2 pb-8 w-full" data-testid="action-confirmation">
    <!-- Success Animation -->
    <div class="relative mb-6" data-testid="confirmation-success-icon">
        <div class="w-24 h-24 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center shadow-lg animate-success-pop">
            <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
            </svg>
        </div>
    </div>

    <!-- Title & Message -->
    <h1 class="!text-3xl md:!text-4xl !leading-tight !font-bold !text-gray-900 mb-2" data-testid="confirmation-title">{title}</h1>
    <p class="!text-base md:!text-[1.125rem] !leading-6 md:!leading-7 !text-gray-600 mb-2" data-testid="confirmation-message">{confirmationMessage}</p>
    
    {#if signedDateMessage}
        <p class="!text-sm md:!text-base !leading-5 md:!leading-6 !text-gray-500 mb-4" data-testid="already-signed-message">
            {signedDateMessage}
        </p>
    {/if}

    <!-- Signature Progress Card -->
    {#if signatureCount && signatureCount > 0}
        <div class="w-full max-w-xl bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-5 mb-8" data-testid="signature-progress-card">
            <div class="flex items-center justify-center gap-2" class:mb-3={signatureGoal}>
                <span class="!text-base md:!text-[1.125rem] !leading-6 md:!leading-7 !font-semibold !text-gray-900" data-testid="signature-count">
                    You're #{signatureCount.toLocaleString()} to sign!
                </span>
            </div>
            
            {#if signatureGoal}
                <!-- Progress bar -->
                <div class="relative h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
                    <div 
                        class="absolute inset-y-0 left-0 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full transition-all duration-1000 ease-out"
                        style="width: {progressPercent}%"
                    ></div>
                </div>
                <p class="!text-base !leading-6 !text-gray-600">
                    <span class="font-semibold text-teal-700">{signatureCount.toLocaleString()}</span> 
                    / {signatureGoal.toLocaleString()} goal
                </p>
                <p class="!text-sm !leading-5 !text-gray-500 mt-1">
                    {#if signaturesRemaining > 0}
                        {getRemainingSignaturesText(signaturesRemaining)}
                    {:else if signatureCount > signatureGoal}
                        {getExceededSignaturesText(signatureCount - signatureGoal)}
                    {:else}
                        Goal reached
                    {/if}
                </p>
            {/if}
        </div>
    {/if}

    <!-- Custom Thank You Message -->
    {#if thankYouMessage}
        <div class="w-full max-w-xl bg-white border border-gray-200 rounded-xl p-5 mb-8 text-center" data-testid="thank-you-message">
            <CkEditorContent
                html={thankYouMessage}
                className="text-sm text-gray-700 mx-auto confirmation-thankyou-content"
            />
        </div>
    {/if}

    <!-- Share Section (only shown if sharing is enabled) -->
    {#if shareUrl && sharingEnabled}
        <div class="w-full max-w-xl" data-testid="share-section">
            <h2 class="!text-lg !leading-7 !font-semibold !text-gray-900 mb-4">
                {safeTranslation('Spread_The_Word_Header', 'Spread the word')}
            </h2>

            <!-- Social Buttons -->
            <div class="grid grid-cols-4 gap-3 mb-4" data-testid="share-buttons">
                <button 
                    class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-[#1877F2] !text-white rounded-xl hover:opacity-90 transition-opacity"
                    on:click={shareToFacebook}
                    aria-label="Share on Facebook"
                    data-testid="share-facebook"
                >
                    <FacebookIcon width="24" height="24" />
                    <span class="!text-xs !font-medium !leading-4">Facebook</span>
                </button>

                <button 
                    class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-black !text-white rounded-xl hover:opacity-90 transition-opacity"
                    on:click={shareToTwitter}
                    aria-label="Share on X"
                    data-testid="share-twitter"
                >
                    <TwitterIcon width="24" height="24" />
                    <span class="!text-xs !font-medium !leading-4">X</span>
                </button>

                <button 
                    class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-[#0A66C2] !text-white rounded-xl hover:opacity-90 transition-opacity"
                    on:click={shareToLinkedIn}
                    aria-label="Share on LinkedIn"
                    data-testid="share-linkedin"
                >
                    <LinkedInIcon width="24" height="24" />
                    <span class="!text-xs !font-medium !leading-4">LinkedIn</span>
                </button>

                <button 
                    class="flex flex-col items-center justify-center gap-1.5 p-3 !min-h-[88px] bg-gray-600 !text-white rounded-xl hover:opacity-90 transition-opacity"
                    on:click={shareViaEmail}
                    aria-label="Share via Email"
                    data-testid="share-email"
                >
                    <EmailIcon width="24" height="24" />
                    <span class="!text-xs !font-medium !leading-4">Email</span>
                </button>
            </div>

            <!-- Copy Link -->
            <p class="!text-sm !leading-5 !text-gray-500 mb-3 text-center">
                {safeTranslation('Personal_Website_Share_Instructions', 'Or copy the link below to share this petition on your personal website.')}
            </p>
            <div class="copy-link-shell flex items-center gap-2 bg-gray-100 rounded-lg p-2" data-testid="share-copy-link">
                <input 
                    type="text" 
                    value={shareUrl} 
                    readonly
                    class="copy-link-input flex-1 min-w-0 bg-transparent !text-base !leading-6 !text-gray-600 px-2 outline-none truncate"
                    data-testid="share-url-input"
                />
                <button 
                    class="copy-link-button inline-flex items-center justify-center px-4 py-2 !min-h-[42px] bg-white border border-gray-300 rounded-lg !text-base !leading-6 !font-medium !text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                    on:click={copyToClipboard}
                    data-testid="copy-link-button"
                >
                    {copied ? `✓ ${safeTranslation('Copied_Label', 'Copied!')}` : safeTranslation('Copy_Link_Button', 'Copy link')}
                </button>
            </div>
        </div>
    {/if}

    <!-- Related Petitions -->
    {#if relatedPetitions.length > 0}
        <div class="w-full max-w-xl mt-8" data-testid="related-petitions">
            <h3 class="!text-lg !leading-7 !font-semibold !text-gray-900 mb-4 text-center">{safeTranslation('Keep_Making_An_Impact_Header', 'Keep making an impact')}</h3>
            <div class="flex flex-col gap-3">
                {#each relatedPetitions as petition}
                    <button
                        class="related-petition-card flex items-center gap-4 px-4 py-3.5 border rounded-xl hover:shadow-sm transition-all text-left group {petition.hasSigned ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200 hover:border-gray-300'}"
                        on:click={() => onSignRelated(petition)}
                        data-testid="related-petition-{petition.id}"
                    >
                        <!-- Thumbnail -->
                        {#if petition.imageUrl}
                            <div class="related-petition-thumb w-[4.5rem] h-[4.5rem] flex-shrink-0 rounded-lg overflow-hidden">
                                <img 
                                    src={petition.imageUrl} 
                                    alt={petition.title}
                                    class="w-full h-full object-cover"
                                />
                            </div>
                        {:else}
                            <div class="related-petition-thumb w-[4.5rem] h-[4.5rem] flex-shrink-0 rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                                <PetitionEmptyStateIcon width="26" height="26" color="rgba(255, 255, 255, 0.6)" />
                            </div>
                        {/if}
                        
                        <!-- Content -->
                        <div class="flex-1 min-w-0 py-0.5">
                            <h4 class="related-petition-title !text-base !leading-6 !font-medium line-clamp-2 transition-colors {petition.hasSigned ? '!text-emerald-900' : '!text-gray-900 group-hover:text-teal-700'}">
                                {petition.title}
                            </h4>
                            {#if petition.hasSigned}
                                <p class="related-petition-signatures !text-sm !leading-5 !text-emerald-700 mt-0.5 flex items-center gap-1.5">
                                    <span class="related-petition-signature-icon">
                                        <CheckIcon width="14" height="14" />
                                    </span>
                                    <span class="related-petition-signature-text">{safeTranslation('Signed_State_Label', 'Signed')}</span>
                                </p>
                            {/if}
                            {#if petition.signatureCount}
                                <p class="related-petition-signatures !text-sm !leading-5 !text-gray-500 {petition.hasSigned ? 'mt-1' : 'mt-1.5'} flex items-center gap-1">
                                    <span class="related-petition-signature-icon">
                                        <UsersIcon width="14" height="14" />
                                    </span>
                                    <span class="related-petition-signature-text">{petition.signatureCount.toLocaleString()} signatures</span>
                                </p>
                            {/if}
                        </div>
                        
                        <!-- Arrow -->
                        <div class="related-petition-arrow flex-shrink-0 transition-colors {petition.hasSigned ? 'text-emerald-600' : 'text-gray-400 group-hover:text-teal-600'}">
                            <CaretRightIcon />
                        </div>
                    </button>
                {/each}
            </div>
        </div>
    {/if}

    <!-- Navigation Buttons -->
    <div class="flex flex-col sm:flex-row gap-3 mt-8 w-full max-w-xl" data-testid="navigation-buttons">
        {#if !hideBackToPetitions}
            <button 
                class="confirmation-nav-button flex-1 px-6 py-3 !min-h-[48px] bg-white border border-gray-300 rounded-lg !text-base !leading-6 !text-gray-700 !font-medium hover:bg-gray-50 transition-colors"
                on:click={onBack}
                data-testid="back-to-petitions-button"
            >
                ← Back to Petitions
            </button>
        {/if}
        <button 
            class="confirmation-nav-button flex-1 px-6 py-3 !min-h-[48px] bg-white border border-gray-300 rounded-lg !text-base !leading-6 !text-gray-700 !font-medium hover:bg-gray-50 transition-colors"
            on:click={onViewOther}
            data-testid="view-other-actions-button"
        >
            {safeTranslation('View_Other_Actions_Button', 'View Other Actions')} →
        </button>
    </div>
</div>

<style>
    @keyframes success-pop {
        0% {
            transform: scale(0);
            opacity: 0;
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }
    .animate-success-pop {
        animation: success-pop 0.5s ease-out forwards;
    }

    /* Prevent host page CSS from inflating fonts/controls in embedded contexts */
    :global(.confirmation-shell button) {
        font-family: inherit;
    }

    :global(.copy-link-shell) {
        background-color: #f3f4f6 !important;
        border-radius: 0.5rem !important;
    }

    :global(.copy-link-input) {
        background: transparent !important;
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        outline: none !important;
        appearance: none !important;
        -webkit-appearance: none !important;
    }

    :global(.copy-link-input:focus),
    :global(.copy-link-input:active),
    :global(.copy-link-input[readonly]) {
        background: transparent !important;
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
    }

    :global(.copy-link-button) {
        background-color: #ffffff !important;
        border: 1px solid #d1d5db !important;
        border-radius: 0.5rem !important;
        color: #374151 !important;
        box-shadow: none !important;
        min-width: 7.5rem !important;
        padding-left: 1rem !important;
        padding-right: 1rem !important;
        flex-shrink: 0 !important;
        box-sizing: border-box !important;
        appearance: none !important;
        -webkit-appearance: none !important;
    }

    :global(.copy-link-button:hover) {
        background-color: #f9fafb !important;
        border-color: #d1d5db !important;
    }

    :global(.related-petition-card) {
        background-color: #ffffff !important;
        border: 1px solid #e5e7eb !important;
        border-radius: 0.75rem !important;
        box-shadow: none !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
        gap: 1rem !important;
        padding: 0.875rem 1rem !important;
        width: 100% !important;
        appearance: none !important;
        -webkit-appearance: none !important;
    }

    :global(.related-petition-card:hover) {
        border-color: #d1d5db !important;
    }

    :global(.related-petition-thumb) {
        width: 4.5rem !important;
        height: 4.5rem !important;
        border-radius: 0.75rem !important;
    }

    :global(.related-petition-title) {
        margin: 0 !important;
        font-size: 1rem !important;
        line-height: 1.5rem !important;
        font-weight: 500 !important;
        color: #111827 !important;
    }

    :global(.related-petition-signatures) {
        margin-bottom: 0 !important;
        align-items: center !important;
    }

    :global(.related-petition-signature-icon) {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        flex: 0 0 auto !important;
        align-self: center !important;
        line-height: 1 !important;
    }

    :global(.related-petition-signature-icon svg) {
        display: block !important;
        flex: 0 0 auto !important;
    }

    :global(.related-petition-signature-text) {
        display: inline-block !important;
        margin: 0 !important;
        padding: 0 !important;
        line-height: inherit !important;
        vertical-align: middle !important;
    }

    :global(.related-petition-arrow) {
        margin-left: auto !important;
        padding-right: 0.25rem !important;
    }

    :global(.confirmation-nav-button) {
        background-color: #ffffff !important;
        border: 1px solid #d1d5db !important;
        border-radius: 0.5rem !important;
        color: #374151 !important;
        box-shadow: none !important;
        appearance: none !important;
        -webkit-appearance: none !important;
    }

    :global(.confirmation-nav-button:hover) {
        background-color: #f9fafb !important;
        border-color: #d1d5db !important;
    }

    /* Remove rich-text edge margins so thank-you text is visually centered in its box. */
    :global(.confirmation-thankyou-content > :first-child) {
        margin-top: 0;
    }
    :global(.confirmation-thankyou-content > :last-child) {
        margin-bottom: 0;
    }
</style>
